// src/componentes/Propiedades/DetallePropiedad.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../Firebase/firebaseconfig";
import notify from "../common/notify";
import { dialogService } from "../common/dialogService";
import "./DetallePropiedad.css";

import {
  listenAdjuntosPropiedad,
  uploadAdjuntoPropiedad,
  deleteAdjuntoPropiedad,
} from "../../services/propiedadAdjuntosService";
import { calcularFinanzasPropiedad, formatPercent } from "../../helpers/finanzasPropiedad";
import { toJsDate } from "../../helpers/fechas";
import { calcularProrrateoParaPropiedad } from "../../helpers/prorrateoCostos";
import { readErpMonthly } from "../../services/erpService";

const fmtFecha = (v) => {
  const d = toJsDate(v);
  return d ? d.toLocaleString() : "-";
};

const ORIGENES_PRORRATEO = {
  propiedad: "Directo por obra/lote",
  obra: "Heredado de obra",
  fraccionamiento: "Heredado de fraccionamiento",
  proyecto: "Heredado de proyecto",
};

const agruparProrrateosPorOrigen = (asignaciones = []) =>
  asignaciones.reduce((acc, asignacion) => {
    const key = asignacion.contexto?.nivel || "otro";
    if (!acc[key]) {
      acc[key] = {
        key,
        label: ORIGENES_PRORRATEO[key] || key,
        total: 0,
        items: [],
      };
    }
    acc[key].total += Number(asignacion.montoAsignado || 0);
    acc[key].items.push(asignacion);
    return acc;
  }, {});

const toMoney = (value) => Number(value || 0).toLocaleString();

const buildCostoLabel = (gasto = {}) =>
  [
    gasto.proveedorNombre || gasto.proveedor || "CxP",
    gasto.referencia && `Ref. ${gasto.referencia}`,
    gasto.obraNombre && `Obra: ${gasto.obraNombre}`,
  ]
    .filter(Boolean)
    .join(" - ");

const tieneContextoFinanciero = (gasto = {}) =>
  Boolean(gasto.propiedadId || gasto.obraId || gasto.fraccionamientoId || gasto.proyectoId);

const currentMonthId = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const DetallePropiedad = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const uid = currentUser?.uid || null;

  const [propiedad, setPropiedad] = useState(null);
  const [deudas, setDeudas] = useState([]);
  const [gastosDirectos, setGastosDirectos] = useState([]);
  const [gastosCxp, setGastosCxp] = useState([]);
  const [propiedadesBase, setPropiedadesBase] = useState([]);
  const [obrasBase, setObrasBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeudas, setLoadingDeudas] = useState(true);
  const [loadingGastos, setLoadingGastos] = useState(true);
  const [error, setError] = useState(null);
  const [erpIssues, setErpIssues] = useState([]);

  // Adjuntos
  const [adjuntos, setAdjuntos] = useState([]);
  const [files, setFiles] = useState([]);
  const [notasAdjunto, setNotasAdjunto] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "propiedades", id));
        if (!snap.exists()) {
          setError("No se encontró la propiedad");
          return;
        }
        setPropiedad({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error(e);
        setError("Error al cargar la propiedad");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoadingGastos(true);
      try {
        const qDirectos = query(
          collection(db, "cxp_deudas"),
          where("propiedadId", "==", id)
        );
        const [directosSnap, cxpSnap, propsSnap, obrasSnap] = await Promise.all([
          getDocs(qDirectos),
          getDocs(collection(db, "cxp_deudas")),
          getDocs(collection(db, "propiedades")),
          getDocs(collection(db, "obras")),
        ]);

        setGastosDirectos(directosSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setGastosCxp(cxpSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPropiedadesBase(propsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setObrasBase(obrasSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error cargando gastos directos de la propiedad:", e);
      } finally {
        setLoadingGastos(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoadingDeudas(true);
      try {
        // Nuevo modelo: cualquier propiedad incluida en la deuda
        const q = query(
          collection(db, "deudas"),
          where("propiedadIds", "array-contains", id)
        );
        const qLegacy = query(
          collection(db, "deudas"),
          where("propiedadId", "==", id)
        );
        const [s, legacySnap] = await Promise.all([getDocs(q), getDocs(qLegacy)]);
        const map = new Map();
        [...s.docs, ...legacySnap.docs].forEach((d) => {
          map.set(d.id, { id: d.id, ...d.data() });
        });
        setDeudas(Array.from(map.values()));
      } catch (e) {
        console.error("Error cargando deudas de la propiedad:", e);
      } finally {
        setLoadingDeudas(false);
      }
    })();
  }, [id]);

  // Suscripción a adjuntos
  useEffect(() => {
    if (!id) return;
    const unsub = listenAdjuntosPropiedad(id, setAdjuntos);
    return () => unsub?.();
  }, [id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const erp = await readErpMonthly(currentMonthId());
        const issues = (erp?.validacionesErp?.rows || []).filter(
          (row) => row.entidadTipo === "propiedad" && row.entidadId === id
        );
        if (alive) setErpIssues(issues);
      } catch (e) {
        console.warn("No se pudieron cargar validaciones ERP de la propiedad:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const handleSelectFiles = (ev) => {
    const fa = Array.from(ev.target.files || []);
    setFiles(fa);
  };

  const canUpload = useMemo(() => !!files.length && !uploading, [files, uploading]);

  const handleDeletePropiedad = async () => {
    if (!await dialogService.confirm("¿Dar de baja este terreno? No se podrá usar para nuevas ventas.")) {
      return;
    }

    try {
      const ref = doc(db, "propiedades", id);

      await updateDoc(ref, {
        activo: false,
        disponible: false,
        estado: "eliminado",
        actualizado: Timestamp.now(),
      });

      notify.success("Terreno dado de baja correctamente.");
      navigate("/app/propiedades");
    } catch (e) {
      console.error(e);
      notify.error("No se pudo dar de baja la propiedad: " + (e?.message || e));
    }
  };

  const handleUpload = async () => {
    if (!files.length) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        await uploadAdjuntoPropiedad(
          id,
          f,
          { uid, notas: notasAdjunto },
          (pct) => setUploadProgress(pct)
        );
      }

      setFiles([]);
      setNotasAdjunto("");
      setUploadProgress(0);
    } catch (e) {
      notify.error("Error al subir archivo(s): " + (e?.message || e));
    } finally {
      setUploading(false);
    }
  };

const getEstadoVisualDeuda = (d) => {
  if (d.activo === false) return "Inactiva";
  if (d.activo === true) return "Activa";

  return d.estatus || "-";
};


  const handleDeleteAdj = async (adj) => {
    if (!await dialogService.confirm(`¿Eliminar "${adj.nombre}"?`)) return;

    try {
      await deleteAdjuntoPropiedad(id, adj.id, adj.storagePath);
    } catch (e) {
      notify.error("No se pudo eliminar: " + (e?.message || e));
    }
  };

  if (loading) return <div className="spinner">Cargando…</div>;
  if (error) return <div className="error">{error}</div>;
  if (!propiedad) return null;

  const {
    folio,
    tipo,
    ubicacion,
    metros2,
    precioContado,
    precioFinanciado,
    fraccNombre,
    fraccionamientoId,
    manzana,
    lote,
    notas,
    codigoHumano,
    creado,
    actualizado,
    bitacora = [],
    imagenes = [],
    documentos = [],
    estado,
    disponible,
    apartadaSocio,
    socioId,
    socioNombre,
    fechaApartadoSocio,
    proyectoId,
    proyectoNombre,
  } = propiedad;

  const money = (n) => Number(n || 0).toLocaleString();
  const deudaPrincipal = deudas[0] || null;
  const prorrateoPropiedad = calcularProrrateoParaPropiedad({
    propiedad,
    gastos: gastosCxp,
    propiedades: propiedadesBase,
    catalogos: {
      obras: new Map(obrasBase.map((o) => [o.id, o])),
      propiedades: new Map(propiedadesBase.map((p) => [p.id, p])),
    },
  });
  const finanzasPropiedad = calcularFinanzasPropiedad({
    propiedad,
    deudas,
    gastosDirectos,
    costosProrrateados: prorrateoPropiedad.totalProrrateado,
  });
  const prorrateosPorOrigen = agruparProrrateosPorOrigen(prorrateoPropiedad.asignaciones);
  const gastosDirectosCapitalizables = gastosDirectos.filter((g) => g.capitalizable === true);
  const gastosDirectosNoCapitalizables = gastosDirectos.filter((g) => g.capitalizable !== true);
  const formulaUtilidad = [
    {
      label: "Ingreso vendido",
      value: finanzasPropiedad.ingresoVendido,
      tone: "plus",
      description: deudaPrincipal ? "Venta/deuda asociada" : "Precio detectado en la propiedad",
    },
    {
      label: "Costos directos capitalizables",
      value: -finanzasPropiedad.costosDirectosCapitalizables,
      tone: "minus",
      description: `${gastosDirectosCapitalizables.length} CxP directas al lote`,
    },
    {
      label: "Costos prorrateados",
      value: -finanzasPropiedad.costosProrrateados,
      tone: "minus",
      description: `${prorrateoPropiedad.asignaciones.length} asignaciones por obra/fracc/proyecto`,
    },
    {
      label: "Utilidad proyectada",
      value: finanzasPropiedad.utilidadProyectadaConProrrateo,
      tone: finanzasPropiedad.utilidadProyectadaConProrrateo >= 0 ? "result-good" : "result-risk",
      description: "Ingreso vendido menos costo total asignado",
    },
  ];
  const topCostosAsignados = [
    ...gastosDirectosCapitalizables.map((gasto) => ({
      id: `directo-${gasto.id}`,
      label: buildCostoLabel(gasto),
      origen: gasto.obraId ? "Directo por obra/lote" : "Directo al lote",
      importe: Number(gasto.importe || 0),
      link: `/app/cxp/deudas/${gasto.id}`,
    })),
    ...prorrateoPropiedad.asignaciones.map((asignacion) => ({
      id: `prorrateo-${asignacion.gastoId}`,
      label: buildCostoLabel(asignacion),
      origen: ORIGENES_PRORRATEO[asignacion.contexto?.nivel] || asignacion.contexto?.nivel || "Prorrateo",
      importe: Number(asignacion.montoAsignado || 0),
      link: `/app/cxp/deudas/${asignacion.gastoId}`,
    })),
  ]
    .sort((a, b) => b.importe - a.importe)
    .slice(0, 6);
  const alertasFinancieras = [
    finanzasPropiedad.utilidadProyectadaConProrrateo < 0 && {
      tipo: "riesgo",
      texto: "La utilidad proyectada con prorrateo es negativa.",
    },
    finanzasPropiedad.ingresoVendido <= 0 && {
      tipo: "riesgo",
      texto: "El lote no tiene precio/ingreso detectable para calcular utilidad.",
    },
    (estado === "vendida" || estado === "vendido") && !deudaPrincipal && {
      tipo: "advertencia",
      texto: "El lote parece vendido, pero no se encontro deuda asociada.",
    },
    gastosCxp.some((g) => g.capitalizable === true && !tieneContextoFinanciero(g)) && {
      tipo: "advertencia",
      texto: "Hay gastos capitalizables sin contexto financiero; pueden quedar fuera del costo del lote.",
    },
    gastosCxp.some(
      (g) =>
        g.capitalizable === true &&
        !g.propiedadId &&
        (g.metodoProrrateo || "ninguno") === "ninguno" &&
        tieneContextoFinanciero(g)
    ) && {
      tipo: "advertencia",
      texto: "Hay gastos capitalizables con contexto, pero sin metodo de prorrateo.",
    },
  ].filter(Boolean);

  return (
    <div className="detalle-wrapper">
      <div className="toolbar">
        <button className="btn ghost" onClick={() => navigate(-1)}>
          ← Volver
        </button>

        <div className="toolbar-right">
          <Link className="btn ghost" to={`/app/propiedades/${id}/editar`}>
            ✎ Editar
          </Link>

          <button
            className="btn"
            onClick={() =>
              navigate("/tickets/new", {
                state: {
                  presetTicket: {
                    entidadTipo: "propiedad",
                    entidadId: id,
                    titulo: `Propiedad ${folio} - `,
                  },
                },
              })
            }
          >
            + Ticket
          </button>

          <button
            className="btn danger"
            onClick={handleDeletePropiedad}
            style={{ marginLeft: 8 }}
          >
            Borrar terreno
          </button>
        </div>
      </div>

      <div className="print-sheet">
        <header className="sheet-header">
          <div>
            <h1>Detalle de Propiedad</h1>
            <div className="muted">
              Folio: <strong>{folio}</strong>
            </div>
          </div>
        </header>

        {erpIssues.length ? (
          <section className="panel erp-context-warning">
            <h3>Validaciones ERP pendientes</h3>
            <div className="finance-alerts">
              {erpIssues.map((issue) => (
                <div key={issue.id} className={`finance-alert finance-alert--${issue.severidad === "critica" ? "riesgo" : "advertencia"}`}>
                  <strong>{issue.titulo}:</strong> {issue.detalle}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="panel">
          <h3>Datos básicos</h3>
          <div className="grid">
            <Field label="Folio" value={folio} />
            <Field label="Código (MZ/LT)" value={codigoHumano || `MZ${manzana}-LT${lote}`} />
            <Field label="Tipo" value={tipo} />
            <Field label="Fraccionamiento" value={fraccNombre || fraccionamientoId} />
            <Field label="Manzana" value={manzana} />
            <Field label="Lote" value={lote} />
            <Field label="m²" value={metros2} />
            <Field label="Ubicación" value={ubicacion} wide />
            <Field label="Estado" value={estado || "-"} />
            <Field label="Disponible" value={disponible ? "Sí" : "No"} />
          </div>
        </section>

        {(apartadaSocio || estado === "apartada_socio") ? (
          <section className="panel">
            <h3>Apartado de socio</h3>
            <div className="grid">
              <Field label="Socio" value={socioNombre || socioId || "-"} />
              <Field
                label="Fecha de apartado"
                value={fmtFecha(fechaApartadoSocio)}
              />
            </div>
            {socioId ? (
              <Link className="btn ghost" to={`/app/socios/${socioId}`} style={{ marginTop: 12 }}>
                Ver socio
              </Link>
            ) : null}
          </section>
        ) : null}

        <section className="panel">
          <h3>Precios</h3>
          <div className="grid">
            <Field label="Precio contado (MXN)" value={`$ ${money(precioContado)}`} />
            <Field label="Precio financiado (MXN)" value={`$ ${money(precioFinanciado)}`} />
          </div>
        </section>

        <section className="panel">
          <h3>Relacion ERP</h3>
          <div className="grid">
            <Field
              label="Proyecto"
              value={
                proyectoId ? (
                  <Link to={`/app/proyectos/${proyectoId}/drive`}>
                    {proyectoNombre || proyectoId}
                  </Link>
                ) : "-"
              }
            />
            <Field
              label="Cliente vinculado"
              value={
                deudaPrincipal?.clienteId ? (
                  <Link to={`/app/cliente/${deudaPrincipal.clienteId}`}>
                    {deudaPrincipal.clienteNombre || deudaPrincipal.clienteId}
                  </Link>
                ) : "-"
              }
            />
            <Field
              label="Deuda / venta"
              value={
                deudaPrincipal?.id ? (
                  <Link to={`/app/cliente/${deudaPrincipal.clienteId}/deudas/${deudaPrincipal.id}`}>
                    {deudaPrincipal.folio || deudaPrincipal.id}
                  </Link>
                ) : "-"
              }
            />
            <Field label="Estado comercial" value={estado || (disponible ? "disponible" : "no disponible")} />
          </div>
        </section>

        <section className="panel">
          <h3>Ficha financiera</h3>
          {alertasFinancieras.length ? (
            <div className="finance-alerts">
              {alertasFinancieras.map((alerta, idx) => (
                <div key={idx} className={`finance-alert finance-alert--${alerta.tipo}`}>
                  {alerta.texto}
                </div>
              ))}
            </div>
          ) : null}
          <div className="finance-grid">
            <FinanceCard
              label="Ingreso vendido"
              value={`$ ${money(finanzasPropiedad.ingresoVendido)}`}
              hint="Venta/deuda asociada o precio de propiedad"
            />
            <FinanceCard
              label="Total cobrado"
              value={`$ ${money(finanzasPropiedad.totalCobrado)}`}
              hint="Pagos positivos registrados"
              tone="good"
            />
            <FinanceCard
              label="Saldo pendiente"
              value={`$ ${money(finanzasPropiedad.saldoPendiente)}`}
              hint="Saldo restante de la deuda"
              tone="warn"
            />
            <FinanceCard
              label="Costos directos"
              value={`$ ${money(finanzasPropiedad.costosDirectos)}`}
              hint={`${finanzasPropiedad.gastosDirectosCount} CxP asociadas al lote`}
            />
            <FinanceCard
              label="Capitalizable"
              value={`$ ${money(finanzasPropiedad.costosDirectosCapitalizables)}`}
              hint="Costo directo que impacta utilidad"
              tone="risk"
            />
            <FinanceCard
              label="Prorrateado"
              value={`$ ${money(finanzasPropiedad.costosProrrateados)}`}
              hint={`${prorrateoPropiedad.asignaciones.length} costos generales asignados`}
              tone="risk"
            />
            <FinanceCard
              label="Costo total asignado"
              value={`$ ${money(finanzasPropiedad.costoTotalAsignado)}`}
              hint="Directo capitalizable + prorrateado"
              tone="risk"
            />
            <FinanceCard
              label="No capitalizable"
              value={`$ ${money(finanzasPropiedad.costosDirectosNoCapitalizables)}`}
              hint="Gasto operativo relacionado"
            />
            <FinanceCard
              label="Utilidad proyectada"
              value={`$ ${money(finanzasPropiedad.utilidadProyectada)}`}
              hint="Ingreso vendido - costos capitalizables directos"
              tone={finanzasPropiedad.utilidadProyectada >= 0 ? "good" : "risk"}
            />
            <FinanceCard
              label="Utilidad c/prorrateo"
              value={`$ ${money(finanzasPropiedad.utilidadProyectadaConProrrateo)}`}
              hint="Ingreso vendido - costo total asignado"
              tone={finanzasPropiedad.utilidadProyectadaConProrrateo >= 0 ? "good" : "risk"}
            />
            <FinanceCard
              label="Realizada c/prorrateo"
              value={`$ ${money(finanzasPropiedad.utilidadRealizadaConProrrateo)}`}
              hint="Cobrado - costo total asignado"
              tone={finanzasPropiedad.utilidadRealizadaConProrrateo >= 0 ? "good" : "risk"}
            />
            <FinanceCard
              label="Margen proyectado"
              value={formatPercent(finanzasPropiedad.margenConProrrateo)}
              hint="Utilidad con prorrateo / ingreso vendido"
            />
          </div>

          <div className="profit-explainer">
            <div className="profit-formula">
              {formulaUtilidad.map((item) => (
                <div key={item.label} className={`profit-step profit-step--${item.tone}`}>
                  <span>{item.label}</span>
                  <strong>
                    {item.value < 0 ? "- " : ""}
                    $ {toMoney(Math.abs(item.value))}
                  </strong>
                  <small>{item.description}</small>
                </div>
              ))}
            </div>

            <div className="top-costs">
              <div className="top-costs-head">
                <h4>Principales costos asignados</h4>
                <span>{topCostosAsignados.length} movimientos</span>
              </div>
              {topCostosAsignados.length ? (
                <div className="top-costs-list">
                  {topCostosAsignados.map((item) => (
                    <Link key={item.id} className="top-cost-row" to={item.link}>
                      <div>
                        <strong>{item.label}</strong>
                        <small>{item.origen}</small>
                      </div>
                      <b>$ {toMoney(item.importe)}</b>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="muted">Sin costos capitalizables asignados a este lote.</div>
              )}
            </div>
          </div>
        </section>

        <section className="panel">
          <h3>Desglose de costos por origen</h3>
          <div className="cost-breakdown-grid">
            <CostOriginCard
              title="Directos al lote"
              amount={finanzasPropiedad.costosDirectosCapitalizables}
              count={gastosDirectosCapitalizables.length}
              description="CxP capitalizables ligadas directamente a esta propiedad."
            />
            {Object.values(prorrateosPorOrigen).map((grupo) => (
              <CostOriginCard
                key={grupo.key}
                title={grupo.label}
                amount={grupo.total}
                count={grupo.items.length}
                description="Costos generales distribuidos a este lote."
              />
            ))}
            <CostOriginCard
              title="No capitalizables"
              amount={finanzasPropiedad.costosDirectosNoCapitalizables}
              count={gastosDirectosNoCapitalizables.length}
              description="Gastos relacionados que no impactan la utilidad del lote."
            />
          </div>
        </section>

        <section className="panel">
          <h3>Costos prorrateados aplicados</h3>
          {loadingGastos ? (
            <div className="spinner">Calculando prorrateos...</div>
          ) : prorrateoPropiedad.asignaciones.length ? (
            <table className="mini">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Referencia</th>
                  <th>Origen</th>
                  <th>Metodo</th>
                  <th>Base</th>
                  <th>Importe</th>
                  <th>Asignado</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {prorrateoPropiedad.asignaciones.map((a) => (
                  <tr key={a.gastoId}>
                    <td>{a.proveedorNombre || "-"}</td>
                    <td>{a.referencia || "-"}</td>
                    <td>{a.contexto.label || a.contexto.nivel}</td>
                    <td>{a.metodo}</td>
                    <td>{a.baseCount} lotes</td>
                    <td>$ {money(a.importe)}</td>
                    <td>$ {money(a.montoAsignado)}</td>
                    <td>{formatPercent(a.factor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="muted">
              No hay costos capitalizables con prorrateo aplicables a este lote.
            </div>
          )}
        </section>

        <section className="panel">
          <h3>Gastos directos del lote</h3>
          {loadingGastos ? (
            <div className="spinner">Cargando gastos...</div>
          ) : gastosDirectos.length ? (
            <table className="mini">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Referencia</th>
                  <th>Importe</th>
                  <th>Saldo</th>
                  <th>Tipo</th>
                  <th>Capitalizable</th>
                  <th>Impacto</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {gastosDirectos.map((g) => (
                  <tr key={g.id}>
                    <td>{g.proveedorNombre || "-"}</td>
                    <td>{g.referencia || "-"}</td>
                    <td>$ {money(g.importe)}</td>
                    <td>$ {money(g.saldoRestante)}</td>
                    <td>{g.tipoCosto || "administrativo"}</td>
                    <td>{g.capitalizable ? "Si" : "No"}</td>
                    <td>{g.capitalizable ? "Utilidad" : "Operativo"}</td>
                    <td>
                      <Link className="btn ghost" to={`/app/cxp/deudas/${g.id}`}>
                        Ver CxP
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="muted">No hay CxP/gastos directos asociados a este lote.</div>
          )}
        </section>

        {notas ? (
          <section className="panel">
            <h3>Notas</h3>
            <div className="field">{notas}</div>
          </section>
        ) : null}

        {!!imagenes.length && (
          <section className="panel">
            <h3>Imágenes</h3>
            <div className="imagenes-grid">
              {imagenes.map((url, i) => (
                <img key={i} src={url} alt={`Propiedad ${i + 1}`} />
              ))}
            </div>
          </section>
        )}

        {!!documentos.length && (
          <section className="panel">
            <h3>Documentos (legacy)</h3>
            <ul className="doc-list">
              {documentos.map((d, i) => (
                <li key={i}>
                  <a href={d.url} target="_blank" rel="noopener noreferrer">
                    {d.name || `Documento ${i + 1}`}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="panel">
          <h3>Adjuntos</h3>

          <div className="adjuntos-uploader">
            <input type="file" multiple onChange={handleSelectFiles} disabled={uploading} />
            <input
              type="text"
              placeholder="Notas del adjunto (opcional)"
              value={notasAdjunto}
              onChange={(e) => setNotasAdjunto(e.target.value)}
              disabled={uploading}
            />
            <button className="btn" onClick={handleUpload} disabled={!canUpload}>
              {uploading ? "Subiendo…" : "Subir"}
            </button>

            {uploading ? (
              <div className="progress">
                <div className="bar" style={{ width: `${uploadProgress}%` }} />
                <span>{uploadProgress}%</span>
              </div>
            ) : null}
          </div>

          <div className="adjuntos-list">
            {adjuntos.length ? (
              <table className="mini">
                <thead>
                  <tr>
                    <th>Archivo</th>
                    <th>Tamaño</th>
                    <th>Tipo</th>
                    <th>Notas</th>
                    <th>Fecha</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {adjuntos.map((adj) => (
                    <tr key={adj.id}>
                      <td>
                        <a href={adj.url} target="_blank" rel="noopener noreferrer">
                          {adj.nombre}
                        </a>
                      </td>
                      <td>{formatBytes(adj.size)}</td>
                      <td>{adj.tipo || "-"}</td>
                      <td>{adj.notas || "-"}</td>
                      <td>
                        {fmtFecha(adj.createdAt)}
                      </td>
                      <td>
                        <button
                          className="btn danger ghost"
                          onClick={() => handleDeleteAdj(adj)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="muted">Sin adjuntos aún.</div>
            )}
          </div>
        </section>

        <section className="panel">
          <h3>Bitácora / Historial</h3>
          <ul className="bitacora">
            {bitacora.length ? (
              bitacora
                .sort((a, b) => (b.ts?.seconds || 0) - (a.ts?.seconds || 0))
                .map((e, i) => (
                  <li key={i}>
                    <strong>{e.tipo}</strong> — {e.detalle}
                    <small className="muted">
                      {toJsDate(e.ts) ? " " + toJsDate(e.ts).toLocaleString() : ""}
                    </small>
                  </li>
                ))
            ) : (
              <li className="muted">Sin eventos</li>
            )}
          </ul>
        </section>

        <section className="panel">
          <h3>Deudas asociadas</h3>
          {loadingDeudas ? (
            <div className="spinner">Cargando deudas…</div>
          ) : deudas.length ? (
            <table className="mini">
              <thead>
                <tr>
                  <th>Folio deuda</th>
                  <th>Cliente</th>
                  <th>Importe</th>
                  <th>Saldo</th>
                  <th>Estatus</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deudas.map((d) => (
                  <tr key={d.id}>
                    <td>{d.folio}</td>
                    <td>{d.clienteNombre || d.clienteId || "-"}</td>
                    <td>$ {money(d.importe ?? d.monto)}</td>
                    <td>$ {money(d.saldoRestante ?? d.saldo)}</td>
                    <td>{getEstadoVisualDeuda(d)}</td>
                    <td>
                      <Link className="btn ghost" to={`/app/cliente/${d.clienteId}/deudas/${d.id}`}>
                        Ver deuda
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="muted">No hay deudas asociadas.</div>
          )}
        </section>

        <section className="panel">
          <div className="grid">
            <Field
              label="Creado"
              value={fmtFecha(creado)}
            />
            <Field
              label="Actualizado"
              value={fmtFecha(actualizado)}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

const Field = ({ label, value, wide }) => (
  <div className={`field ${wide ? "wide" : ""}`}>
    <div className="label">{label}</div>
    <div className="value">{value ?? "-"}</div>
  </div>
);

const FinanceCard = ({ label, value, hint, tone = "neutral" }) => (
  <div className={`finance-card finance-card--${tone}`}>
    <div className="finance-label">{label}</div>
    <div className="finance-value">{value}</div>
    {hint ? <div className="finance-hint">{hint}</div> : null}
  </div>
);

const CostOriginCard = ({ title, amount, count, description }) => (
  <div className="cost-origin-card">
    <div>
      <div className="finance-label">{title}</div>
      <div className="finance-value">$ {Number(amount || 0).toLocaleString()}</div>
    </div>
    <div className="finance-hint">
      {count} movimiento{count === 1 ? "" : "s"} · {description}
    </div>
  </div>
);

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(Math.max(bytes, 1)) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
}

export default DetallePropiedad;
